import React from 'react'
import { PlusIcon, XIcon, UserIcon } from 'lucide-react'
import { getContactDisplayName } from '../../lib/leadHelpers'
import { formatPhoneInput } from './phoneFormat'
import type { CustomerContact } from './types'

interface CustomerContactsFieldsProps {
  contacts: CustomerContact[]
  onChange: (contacts: CustomerContact[]) => void
  compact?: boolean
}

export function CustomerContactsFields({ contacts, onChange, compact }: CustomerContactsFieldsProps) {
  const inputClass = compact
    ? 'w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm'
    : 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500'

  const updateContact = (id: string, field: string, value: string) => {
    onChange(contacts.map((c) => (c.id === id ? { ...c, [field]: value } : c)))
  }

  const addContact = () => {
    onChange([
      ...contacts,
      {
        id: Date.now().toString(),
        first_name: '',
        last_name: '',
        title: '',
        phone: '',
        cell: '',
        email: '',
      },
    ])
  }

  const removeContact = (id: string) => {
    onChange(contacts.filter((c) => c.id !== id))
  }

  return (
    <div>
      {!compact && (
        <div className="flex items-center gap-2 mb-6">
          <UserIcon className="w-6 h-6 text-blue-600" />
          <h2 className="text-xl font-semibold text-gray-900">Contact Information</h2>
        </div>
      )}
      <div className="space-y-4">
        {contacts.map((contact, index) => (
          <div key={contact.id} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {getContactDisplayName(contact)} {index === 0 && '(Primary)'}
              </h3>
              {contacts.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeContact(contact.id)}
                  className="p-1 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <XIcon className="w-5 h-5 text-red-600" />
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name {index === 0 ? '*' : ''}
                </label>
                <input
                  type="text"
                  required={index === 0}
                  value={contact.first_name}
                  onChange={(e) => updateContact(contact.id, 'first_name', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name {index === 0 ? '*' : ''}
                </label>
                <input
                  type="text"
                  required={index === 0}
                  value={contact.last_name}
                  onChange={(e) => updateContact(contact.id, 'last_name', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                <input
                  type="text"
                  value={contact.title || ''}
                  onChange={(e) => updateContact(contact.id, 'title', e.target.value)}
                  placeholder="e.g., Manager, Director, etc."
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={contact.email}
                  onChange={(e) => updateContact(contact.id, 'email', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                <input
                  type="tel"
                  value={contact.phone}
                  onChange={(e) => updateContact(contact.id, 'phone', formatPhoneInput(e.target.value))}
                  placeholder="(123) 456-7890"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Cell</label>
                <input
                  type="tel"
                  value={contact.cell}
                  onChange={(e) => updateContact(contact.id, 'cell', formatPhoneInput(e.target.value))}
                  placeholder="(123) 456-7890"
                  className={inputClass}
                />
              </div>
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={addContact}
          className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          <PlusIcon className="w-5 h-5" />
          Add Contact
        </button>
      </div>
    </div>
  )
}
